import React from 'react';
import { Field, ErrorMessage } from 'formik';
import { Checkbox, Typography, Radio, Select, Row, Col, Divider, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { colors } from '../constants';
import {
  StepTitle, FormSection, FormItem, SectionTitle, ErrorText,
  InfoBox, LargeInput, LargeSelect, LargeRadio
} from '../styledComponents';

const { Paragraph, Text } = Typography;
const { Option } = Select;

const StepResearchOptIns = ({ values, setFieldValue }) => (
  <>
    <StepTitle level={4}>Research & Additional Options</StepTitle>
    <InfoBox>
      <div className="icon"><InfoCircleOutlined /></div>
      <div className="content">
        <Paragraph style={{ margin: 0, color: colors.text }}>
          These final steps help us advance heart health research and provide you with additional resources
          and support.
        </Paragraph>
      </div>
    </InfoBox>
    
    <FormSection>
      <FormItem>
        <SectionTitle>Data Sharing Permission</SectionTitle>
        <Paragraph style={{ fontSize: '14px', color: colors.text, marginTop: '6px', marginBottom: '10px', lineHeight: '1.5', fontWeight: '400' }}>
          Data is essential for keeping our bus operational and plays a critical role in helping us potentially save lives from heart attacks or strokes. 
          We would like your permission to use your data, ensuring that no identifiable information will be included, for our white paper, research or policy/laws. 
          Rest assured, we will never sell your personal information. Is it okay for us to use your data in this way?
        </Paragraph>
        <div>
          <Radio.Group
            value={values.dataSharingPermission}
            onChange={(e) => setFieldValue('dataSharingPermission', e.target.value)}
          >
            <Space direction="vertical">
              <LargeRadio value="yes">Yes, I permit anonymized data sharing for research purposes</LargeRadio>
              <LargeRadio value="no">No, do not share my data</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="dataSharingPermission" component={ErrorText} />
      </FormItem>
    </FormSection>
    
    <FormSection>
      <FormItem>
        <SectionTitle>Heart Guard Champion Program</SectionTitle>
        <Paragraph style={{ fontSize: '14px', color: colors.text, marginTop: '6px', marginBottom: '10px', lineHeight: '1.5', fontWeight: '400' }}>
          Would you like to become a member BHA&apos;s &apos;Heart Guard Champion&apos; advocacy program? This program is dedicated to the principles of justice, righteousness, and equity. 
          Our mission is to combat injustice, inequity, and unrighteousness within the healthcare system and beyond. We invite you to join us in our fight by engaging in policy reform, 
          addressing systemic issues, and raising your voice for our patient community. Together, we can create a more just and equitable world. Join Heart Guard today and be a part of the change!
        </Paragraph>
        <div>
          <Radio.Group
            value={values.heartGuardChampionOptIn}
            onChange={(e) => setFieldValue('heartGuardChampionOptIn', e.target.value)}
          >
            <Space direction="vertical">
              <LargeRadio value="yes">Yes, sign me up for the Heart Guard Champion program</LargeRadio>
              <LargeRadio value="no">No, thank you</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="heartGuardChampionOptIn" component={ErrorText} />
      </FormItem>
    </FormSection>
    
    <FormSection>
      <Divider style={{ margin: '0 0 24px' }}>
        <Text style={{ color: colors.text, fontSize: '14px' }}>Additional Family Member Testing</Text>
      </Divider>
      
      <FormItem>
        <Checkbox
          checked={values.includeSpouseOrMinor}
          onChange={(e) => setFieldValue('includeSpouseOrMinor', e.target.checked)}
        >
          I would like to request an Lp(a) test for a spouse or dependent family member
        </Checkbox>
      </FormItem>
      
      {values.includeSpouseOrMinor && (
        <>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <FormItem>
                <SectionTitle>First Name</SectionTitle>
                <Field name="spouseFirstName">
                  {({ field }) => (
                    <LargeInput
                      {...field}
                      placeholder="Enter first name"
                    />
                  )}
                </Field>
              </FormItem>
            </Col>
            <Col xs={24} sm={12}>
              <FormItem>
                <SectionTitle>Last Name</SectionTitle>
                <Field name="spouseLastName">
                  {({ field }) => (
                    <LargeInput
                      {...field}
                      placeholder="Enter last name"
                    />
                  )}
                </Field>
              </FormItem>
            </Col>
          </Row>
          
          <FormItem>
            <SectionTitle>Email Address</SectionTitle>
            <Field name="spouseEmail">
              {({ field }) => (
                <LargeInput
                  {...field}
                  placeholder="Enter email address"
                  type="email"
                />
              )}
            </Field>
          </FormItem>
          
          <FormItem>
            <SectionTitle>Relationship</SectionTitle>
            <LargeSelect
              style={{ width: '100%' }}
              placeholder="Select Relationship"
              value={values.spouseRelationship}
              onChange={(value) => setFieldValue('spouseRelationship', value)}
            >
              <Option value="spouse">Spouse</Option>
              <Option value="child">Child</Option>
              <Option value="parent">Parent</Option>
              <Option value="sibling">Sibling</Option>
              <Option value="other">Other</Option>
            </LargeSelect>
          </FormItem>
          
          <FormItem>
            <SectionTitle>Date of Birth</SectionTitle>
            <DatePicker
              selected={values.spouseDateOfBirth ? moment(values.spouseDateOfBirth, "MM/DD/YYYY").toDate() : null}
              onChange={(date) => {
                const formattedDate = date ? moment(date).format("MM/DD/YYYY") : '';
                setFieldValue('spouseDateOfBirth', formattedDate);
              }}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select date of birth"
              maxDate={new Date()}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              openToDate={new Date(1990, 0, 1)}
              customInput={<LargeInput />}
            />
          </FormItem>
        </>
      )}
    </FormSection>
  </>
);

export default StepResearchOptIns;
